import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { H2 } from 'foundations-components/transitional/components/H2';
import { PIPELINE_WITH_PERMISSIONS } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { PIPELINE_STAGE_IN_PIPELINE } from '../../../resolvers/CustomResolverReferenceTypes';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { createCachedCustomResolver } from '../../../resolvers/createCachedCustomResolver';
import { useHttpClient } from '../../../client/HttpClientContext';
import { useObjectTypeId } from '../../../v2/hooks/useObjectTypeId';
import { referenceToOptionWithReferenceV2 } from '../../../utils/referenceLiteSearchSelectUtils';
import { useObjectId } from '../../../v2/hooks/useObjectId';
import { useGetLoadStageReferences } from '../hooks/useGetLoadStageReferences';
import { useRecordInitialPipelineAndStage } from '../hooks/useRecordInitialPipelineAndStage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { isBETPortal } from '../../../v2/utils/isBETPortal';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { TIME_TOKENS } from '../../../utils/timeTokens';
import { RESOLVER_DEBOUNCE_KEY } from '../../../utils/timeTokens';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PipelineModal = props => {
  const {
    onConfirm,
    onCancel,
    defaultPipelineId,
    defaultPipelineStageId,
    inputMode,
    stageChangeValidatorLoading,
    showAutoSaveText = false
  } = props;
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const httpClient = useHttpClient();
  const isPortal53 = isBETPortal();
  const [pipelineId, setPipelineId] = useState(defaultPipelineId);
  const [pipelineStageId, setPipelineStageId] = useState(defaultPipelineStageId);
  const [stageReference, setStageReference] = useState(null);
  const {
    recordPipelineId,
    recordStageId,
    propertyValuesLoading
  } = useRecordInitialPipelineAndStage({
    httpClient,
    objectTypeId,
    inputMode,
    objectId
  });
  const loadStageReferences = useGetLoadStageReferences({
    httpClient,
    objectTypeId,
    pipelineId,
    inputMode,
    objectId
  });

  // NOTE: we cannot use `usePropertyComponentResolver` here because we need
  // `PIPELINE_WITH_PERMISSIONS` to ensure that PLP-hidden pipelines are disabled.
  // Making `PIPELINE_WITH_PERMISSIONS` the default for pipeline properties in
  // `getReferenceTypeFromProperty` (instead of `PIPELINE`) would break
  // filters/workflows by preventing users from selecting these pipelines
  const pipelineResolver = createStandardCachedResolver(PIPELINE_WITH_PERMISSIONS, objectTypeId, {
    httpClient,
    debouncePeriod: TIME_TOKENS[RESOLVER_DEBOUNCE_KEY]
  });

  // parameterize the resolver with `pipelineId` so it only shows stages in the selected pipeline
  const pipelineStageResolver = createCachedCustomResolver(PIPELINE_STAGE_IN_PIPELINE, objectTypeId, {
    pipelineId: pipelineId === null ? undefined : pipelineId,
    httpClient
  });

  // Use `onSelectedOptionChange` to handle changes instead of `onChange`,
  // because we need access to the selected `Reference` to read its
  // pipelineId (changing stage should also change pipeline).
  const handlePipelineChange = useCallback(e => {
    const reference = e.target.value.reference;
    const newPipelineId = `${e.target.value.value}`;
    const defaultStageId = reference && reference.additionalProperties && reference.additionalProperties.defaultPipelineStageId ? `${reference.additionalProperties.defaultPipelineStageId}` : null;
    const newStageId = !propertyValuesLoading && newPipelineId === recordPipelineId && recordStageId ? recordStageId : defaultStageId;
    setPipelineId(newPipelineId);
    setPipelineStageId(isPortal53 ? null : newStageId);
  }, [recordPipelineId, recordStageId, propertyValuesLoading, isPortal53]);
  const handlePipelineStageChange = useCallback(e => {
    const reference = e.target.value.reference;
    const newStageId = `${e.target.value.value}`;
    const newPipelineId = reference && reference.additionalProperties && reference.additionalProperties.pipelineId ? `${reference.additionalProperties.pipelineId}` : null;
    setPipelineId(newPipelineId);
    setPipelineStageId(newStageId);
    setStageReference(reference);
  }, []);
  const handleConfirm = useCallback(() => {
    onConfirm({
      pipelineId,
      pipelineStageId,
      stageReference: stageReference !== null && stageReference !== void 0 ? stageReference : undefined
    });
  }, [onConfirm, pipelineId, pipelineStageId, stageReference]);
  const disabled = !(pipelineId && pipelineStageId);
  const confirmLabel = showAutoSaveText ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPipelineV2.save"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customerDataProperties.PropertyInputPipelineV2.apply"
  });
  return /*#__PURE__*/_jsxs(Modal, {
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCancel
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [propertyValuesLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered"
      }), !propertyValuesLoading && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputPipelineV2.pipeline"
          }),
          children: /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, {
            referenceToOption: referenceToOptionWithReferenceV2,
            onSelectedOptionChange: handlePipelineChange,
            resolver: pipelineResolver,
            value: pipelineId
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataProperties.PropertyInputPipelineV2.stage"
          }),
          children: /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, {
            referenceToOption: referenceToOptionWithReferenceV2,
            onSelectedOptionChange: handlePipelineStageChange,
            resolver: pipelineStageResolver,
            value: pipelineStageId,
            loadReferences: loadStageReferences
            // The reason we don't need to add cache={false} here is because the record can't get saved while the modal is visible
            // For a smoother experience, we can utilize UISelect's option cache since we don't need to refetch validation when the input opens
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.confirmDisabledTooltip"
        }),
        disabled: !disabled,
        children: /*#__PURE__*/_jsx(Button, {
          use: "primary",
          onClick: handleConfirm,
          disabled: disabled || stageChangeValidatorLoading,
          children: confirmLabel
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onCancel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPipelineV2.cancel"
        })
      })]
    })]
  });
};
PipelineModal.displayName = 'PipelineModal';
export default PipelineModal;